import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { MealTime, ScheduleByDate, Recipe, MealItem, ScheduleStats, NutritionData, UserMealTimeConfig } from '../types';

interface UseScheduleProps {
  initialSchedule: ScheduleByDate;
  onScheduleChange: (schedule: ScheduleByDate) => void;
  customMealTimes?: UserMealTimeConfig[];
}

interface UseScheduleReturn {
  // State
  scheduleByDate: ScheduleByDate;
  setScheduleByDate: (schedule: ScheduleByDate | ((prev: ScheduleByDate) => ScheduleByDate)) => void;
  selectedDate: string;
  setSelectedDate: (date: string) => void;
  currentSchedule: MealTime[];

  // Handlers
  handleMealComplete: (mealSlot: string, itemId: string) => void;
  handleAddDishStart: (mealTitle: string, navigate: (view: string, params?: any) => void) => void;
  handleAddDishConfirm: (recipe: Recipe, addDishTargetMeal: string | null, currentSchedule: MealTime[], goBack: () => void, showToastMessage: (msg: string) => void, awardXP: (amount: number) => void) => void;
  handleAddToPlanConfirm: (date: string, meal: string, recipe: Recipe | null, options?: { quantity?: number; note?: string }, showToastMessage?: (msg: string) => void, awardXP?: (amount: number) => void) => void;
  addDishTargetMeal: string | null;
  setAddDishTargetMeal: (meal: string | null) => void;

  // 新增：批量操作
  copySchedule: (sourceDate: string, targetDate: string, mealTitles?: string[]) => void;
  deleteSchedule: (date: string, mealTitles?: string[]) => void;
  moveSchedule: (sourceDate: string, targetDate: string, mealTitles?: string[]) => void;

  // 新增：计划项管理
  deleteMealItem: (date: string, mealTitle: string, itemId: string) => void;
  updateMealItem: (date: string, mealTitle: string, itemId: string, updates: Partial<MealItem>) => void;

  // 新增：统计计算
  calculateStats: (startDate: string, endDate: string) => ScheduleStats;
  getDayNutrition: (date: string) => NutritionData;

  // 新增：购物清单生成
  generateShoppingListFromSchedule: (dates: string[]) => { name: string; amount: string; linkedIngredientId?: string }[];
}

// 默认时段配置
const DEFAULT_MEAL_TIMES: UserMealTimeConfig[] = [
  {
    id: 'breakfast',
    time: "08:00",
    title: "早餐",
    icon: "wb_twilight",
    sortOrder: 1,
    isDefault: true,
    bgClass: "bg-[#e8f5e9] dark:bg-[#1b5e20] border-[#c8e6c9] dark:border-[#2e7d32]",
    borderClass: "border-[#dbe6db] dark:border-[#3e3e3e]",
    titleColorClass: "text-[#1b5e20] dark:text-[#e8f5e9]"
  },
  {
    id: 'lunch',
    time: "12:30",
    title: "午餐",
    icon: "sunny",
    sortOrder: 2,
    isDefault: true,
    bgClass: "bg-[#fff3e0] dark:bg-[#4e342e] border-[#ffcc80] dark:border-[#8d6e63]",
    borderClass: "border-[#ffe0b2] dark:border-[#5d4037]",
    titleColorClass: "text-[#e65100] dark:text-[#ffcc80]"
  },
  {
    id: 'dinner',
    time: "18:30",
    title: "晚餐",
    icon: "nights_stay",
    sortOrder: 3,
    isDefault: true,
    bgClass: "bg-[#eceff1] dark:bg-[#263238] border-[#b0bec5] dark:border-[#37474f]",
    borderClass: "border-[#cfd8dc] dark:border-[#455a64]",
    titleColorClass: "text-[#455a64] dark:text-[#cfd8dc]"
  }
];

const createDefaultSchedule = (customMealTimes?: UserMealTimeConfig[]): MealTime[] => {
  const mealTimes = customMealTimes && customMealTimes.length > 0
    ? customMealTimes.sort((a, b) => a.sortOrder - b.sortOrder)
    : DEFAULT_MEAL_TIMES;

  return mealTimes.map(config => ({
    time: config.time,
    title: config.title,
    icon: config.icon,
    status: '未开始' as const,
    statusColorClass: "text-[#607d8b] bg-[#cfd8dc]",
    bgClass: config.bgClass || "bg-[#f5f5f5] dark:bg-[#424242] border-[#e0e0e0] dark:border-[#616161]",
    borderClass: config.borderClass || "border-[#e0e0e0] dark:border-[#616161]",
    titleColorClass: config.titleColorClass || "text-[#616161] dark:text-[#e0e0e0]",
    items: []
  }));
};

export const useSchedule = ({ initialSchedule, onScheduleChange, customMealTimes }: UseScheduleProps): UseScheduleReturn => {
  const [scheduleByDate, setScheduleByDateInternal] = useState<ScheduleByDate>(initialSchedule);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [addDishTargetMeal, setAddDishTargetMeal] = useState<string | null>(null);

  // 用于存储上一次的 schedule 值，避免重复保存
  const lastSavedScheduleRef = useRef<string>('');
  // 用于防抖的定时器
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sync with external schedule when it changes
  const setScheduleByDate = useCallback((newSchedule: ScheduleByDate | ((prev: ScheduleByDate) => ScheduleByDate)) => {
    setScheduleByDateInternal(prev => {
      const updated = typeof newSchedule === 'function' ? newSchedule(prev) : newSchedule;
      const scheduleJson = JSON.stringify(updated);
      
      // 如果数据没有变化，不触发保存
      if (lastSavedScheduleRef.current === scheduleJson) {
        console.log('[useSchedule] Schedule unchanged, skipping save');
        return updated;
      }
      
      console.log('[useSchedule] setScheduleByDate called, updating schedule:', updated);
      console.log('[useSchedule] Number of dates in schedule:', Object.keys(updated).length);
      
      // 清除之前的定时器
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      // 防抖保存：延迟 500ms 保存，避免频繁写入
      saveTimeoutRef.current = setTimeout(() => {
        lastSavedScheduleRef.current = scheduleJson;
        onScheduleChange(updated);
      }, 500);
      
      return updated;
    });
  }, [onScheduleChange]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Computed: Current schedule for selected date
  const currentSchedule = useMemo(() => {
    const schedule = scheduleByDate[selectedDate];
    if (schedule && schedule.length > 0) {
      return schedule;
    }
    return createDefaultSchedule(customMealTimes);
  }, [scheduleByDate, selectedDate, customMealTimes]);

  // Handle meal completion
  const handleMealComplete = useCallback((mealSlot: string, itemId: string) => {
    setScheduleByDate(prev => ({
      ...prev,
      [selectedDate]: (prev[selectedDate] || []).map(slot => {
        if (slot.title === mealSlot) {
          const updatedItems = slot.items.map(item =>
            item.id === itemId ? { ...item, completed: !item.completed, completedDate: !item.completed ? new Date().toISOString() : undefined } : item
          );
          const allCompleted = updatedItems.every(i => i.completed);
          const anyCompleted = updatedItems.some(i => i.completed);
          return {
            ...slot,
            items: updatedItems,
            status: allCompleted ? '已完成' : anyCompleted ? '进行中' : '未开始',
            statusColorClass: allCompleted ? 'text-[#2e7d32] bg-[#c8e6c9]' : anyCompleted ? 'text-[#e65100] bg-[#ffe0b2]' : 'text-[#607d8b] bg-[#cfd8dc]'
          };
        }
        return slot;
      })
    }));
  }, [selectedDate, setScheduleByDate]);

  // Handle adding dish to plan (from add dish button)
  const handleAddDishStart = useCallback((mealTitle: string, navigate: ((view: string, params?: any) => void) | ((path: string) => void)) => {
    setAddDishTargetMeal(mealTitle);
    // Support both old navigation and react-router navigate
    navigate('/recipes?mode=select');
  }, []);

  const handleAddDishConfirm = useCallback((recipe: Recipe, addDishTargetMeal: string | null, currentSchedule: MealTime[], goBack: () => void, showToastMessage: (msg: string) => void, awardXP: (amount: number) => void) => {
    if (!addDishTargetMeal) return;
    setScheduleByDate(prev => ({
      ...prev,
      [selectedDate]: (prev[selectedDate] || currentSchedule).map(slot => {
        if (slot.title === addDishTargetMeal) {
          return {
            ...slot,
            items: [...slot.items, {
              id: `m-${Date.now()}`,
              name: recipe.name,
              calories: recipe.calories,
              completed: false,
              icon: 'restaurant',
              iconColorClass: 'text-primary',
              recipeId: recipe.id
            }]
          };
        }
        return slot;
      })
    }));
    setAddDishTargetMeal(null);
    goBack();
    showToastMessage(`已添加 "${recipe.name}" 到计划！`);
    awardXP(5);
  }, [selectedDate, setScheduleByDate]);

  const handleAddToPlanConfirm = useCallback((
    date: string,
    meal: string,
    recipe: Recipe | null,
    options?: { quantity?: number; note?: string },
    showToastMessage?: (msg: string) => void,
    awardXP?: (amount: number) => void
  ) => {
    if (!recipe) return;

    setScheduleByDate(prev => {
      const targetSchedule = prev[date] && prev[date].length > 0
        ? prev[date]
        : createDefaultSchedule(customMealTimes);

      return {
        ...prev,
        [date]: targetSchedule.map(slot => {
          if (slot.title === meal) {
            const quantity = options?.quantity || 1;
            const newItem: MealItem = {
              id: `m-${Date.now()}`,
              name: recipe.name,
              calories: recipe.calories * quantity,
              completed: false,
              icon: 'restaurant',
              iconColorClass: 'text-primary',
              recipeId: recipe.id,
              quantity,
              note: options?.note,
              recipeImage: recipe.image,
              cookTime: recipe.time,
              ingredients: recipe.ingredients?.map(ing => ({
                name: ing.name,
                amount: ing.amount
              }))
            };
            return {
              ...slot,
              items: [...slot.items, newItem]
            };
          }
          return slot;
        })
      };
    });
    showToastMessage?.("已加入计划！");
    awardXP?.(5);
  }, [setScheduleByDate, customMealTimes]);

  // 批量操作：复制计划
  const copySchedule = useCallback((sourceDate: string, targetDate: string, mealTitles?: string[]) => {
    setScheduleByDate(prev => {
      const sourceSchedule = prev[sourceDate];
      if (!sourceSchedule) return prev;

      const targetSchedule = prev[targetDate] && prev[targetDate].length > 0
        ? [...prev[targetDate]]
        : createDefaultSchedule(customMealTimes);

      const updatedTargetSchedule = targetSchedule.map(slot => {
        if (!mealTitles || mealTitles.includes(slot.title)) {
          const sourceSlot = sourceSchedule.find(s => s.title === slot.title);
          if (sourceSlot) {
            return {
              ...slot,
              items: [...slot.items, ...sourceSlot.items.map(item => ({
                ...item,
                id: `m-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                completed: false,
                completedDate: undefined
              }))]
            };
          }
        }
        return slot;
      });

      return {
        ...prev,
        [targetDate]: updatedTargetSchedule
      };
    });
  }, [setScheduleByDate, customMealTimes]);

  // 批量操作：删除计划
  const deleteSchedule = useCallback((date: string, mealTitles?: string[]) => {
    setScheduleByDate(prev => {
      const schedule = prev[date];
      if (!schedule) return prev;

      if (!mealTitles) {
        // 删除整天的计划
        const { [date]: _, ...rest } = prev;
        return rest;
      }

      // 删除指定时段的计划项
      return {
        ...prev,
        [date]: schedule.map(slot =>
          mealTitles.includes(slot.title)
            ? { ...slot, items: [], status: '未开始' as const, statusColorClass: "text-[#607d8b] bg-[#cfd8dc]" }
            : slot
        )
      };
    });
  }, [setScheduleByDate]);

  // 批量操作：移动计划
  const moveSchedule = useCallback((sourceDate: string, targetDate: string, mealTitles?: string[]) => {
    setScheduleByDate(prev => {
      const sourceSchedule = prev[sourceDate];
      if (!sourceSchedule) return prev;

      const targetSchedule = prev[targetDate] && prev[targetDate].length > 0
        ? [...prev[targetDate]]
        : createDefaultSchedule(customMealTimes);

      const updatedTargetSchedule = targetSchedule.map(slot => {
        if (!mealTitles || mealTitles.includes(slot.title)) {
          const sourceSlot = sourceSchedule.find(s => s.title === slot.title);
          if (sourceSlot) {
            return {
              ...slot,
              items: [...slot.items, ...sourceSlot.items.map(item => ({
                ...item,
                id: `m-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                completed: false,
                completedDate: undefined
              }))]
            };
          }
        }
        return slot;
      });

      // 从源日期移除
      const updatedSourceSchedule = sourceSchedule.map(slot =>
        (!mealTitles || mealTitles.includes(slot.title))
          ? { ...slot, items: [], status: '未开始' as const, statusColorClass: "text-[#607d8b] bg-[#cfd8dc]" }
          : slot
      );

      return {
        ...prev,
        [sourceDate]: updatedSourceSchedule,
        [targetDate]: updatedTargetSchedule
      };
    });
  }, [setScheduleByDate, customMealTimes]);

  // 删除单个计划项
  const deleteMealItem = useCallback((date: string, mealTitle: string, itemId: string) => {
    setScheduleByDate(prev => {
      const schedule = prev[date];
      if (!schedule) return prev;

      return {
        ...prev,
        [date]: schedule.map(slot => {
          if (slot.title === mealTitle) {
            const updatedItems = slot.items.filter(item => item.id !== itemId);
            const allCompleted = updatedItems.every(i => i.completed);
            const anyCompleted = updatedItems.some(i => i.completed);
            return {
              ...slot,
              items: updatedItems,
              status: allCompleted ? '已完成' : anyCompleted ? '进行中' : '未开始',
              statusColorClass: allCompleted ? 'text-[#2e7d32] bg-[#c8e6c9]' : anyCompleted ? 'text-[#e65100] bg-[#ffe0b2]' : 'text-[#607d8b] bg-[#cfd8dc]'
            };
          }
          return slot;
        })
      };
    });
  }, [setScheduleByDate]);

  // 更新单个计划项
  const updateMealItem = useCallback((date: string, mealTitle: string, itemId: string, updates: Partial<MealItem>) => {
    setScheduleByDate(prev => {
      const schedule = prev[date];
      if (!schedule) return prev;

      return {
        ...prev,
        [date]: schedule.map(slot => {
          if (slot.title === mealTitle) {
            return {
              ...slot,
              items: slot.items.map(item =>
                item.id === itemId ? { ...item, ...updates } : item
              )
            };
          }
          return slot;
        })
      };
    });
  }, [setScheduleByDate]);

  // 计算统计数据
  const calculateStats = useCallback((startDate: string, endDate: string): ScheduleStats => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dailyStats = [];
    let totalMeals = 0;
    let completedMeals = 0;
    let totalCalories = 0;
    const nutritionTotals: NutritionData = { calories: 0, protein: 0, fat: 0, carbs: 0, fiber: 0 };

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      const schedule = scheduleByDate[dateStr];

      if (schedule) {
        let dayMeals = 0;
        let dayCompleted = 0;
        let dayCalories = 0;

        schedule.forEach(slot => {
          slot.items.forEach(item => {
            dayMeals++;
            totalMeals++;
            dayCalories += item.calories;
            totalCalories += item.calories;

            if (item.completed) {
              dayCompleted++;
              completedMeals++;
            }

            if (item.nutrition) {
              nutritionTotals.calories += item.nutrition.calories;
              nutritionTotals.protein += item.nutrition.protein;
              nutritionTotals.fat += item.nutrition.fat;
              nutritionTotals.carbs += item.nutrition.carbs;
              nutritionTotals.fiber += item.nutrition.fiber;
            }
          });
        });

        dailyStats.push({
          date: dateStr,
          totalMeals: dayMeals,
          completedMeals: dayCompleted,
          totalCalories: dayCalories
        });
      }
    }

    const daysCount = dailyStats.length || 1;

    return {
      dateRange: { start: startDate, end: endDate },
      totalMeals,
      completedMeals,
      completionRate: totalMeals > 0 ? Math.round((completedMeals / totalMeals) * 100) : 0,
      totalCalories,
      averageDailyCalories: Math.round(totalCalories / daysCount),
      nutritionTotals,
      dailyStats
    };
  }, [scheduleByDate]);

  // 获取某天的营养数据
  const getDayNutrition = useCallback((date: string): NutritionData => {
    const schedule = scheduleByDate[date];
    const nutrition: NutritionData = { calories: 0, protein: 0, fat: 0, carbs: 0, fiber: 0 };

    if (!schedule) return nutrition;

    schedule.forEach(slot => {
      slot.items.forEach(item => {
        if (item.nutrition) {
          nutrition.calories += item.nutrition.calories;
          nutrition.protein += item.nutrition.protein;
          nutrition.fat += item.nutrition.fat;
          nutrition.carbs += item.nutrition.carbs;
          nutrition.fiber += item.nutrition.fiber;
        } else {
          nutrition.calories += item.calories;
        }
      });
    });

    return nutrition;
  }, [scheduleByDate]);

  // 从计划生成购物清单
  const generateShoppingListFromSchedule = useCallback((dates: string[]): { name: string; amount: string; linkedIngredientId?: string }[] => {
    const ingredientMap = new Map<string, { name: string; amount: string; linkedIngredientId?: string; count: number }>();

    dates.forEach(date => {
      const schedule = scheduleByDate[date];
      if (schedule) {
        schedule.forEach(slot => {
          slot.items.forEach(item => {
            if (item.ingredients) {
              item.ingredients.forEach(ing => {
                const key = ing.name.toLowerCase();
                const existing = ingredientMap.get(key);
                if (existing) {
                  existing.count += item.quantity || 1;
                } else {
                  ingredientMap.set(key, {
                    name: ing.name,
                    amount: ing.amount,
                    linkedIngredientId: item.recipeId,
                    count: item.quantity || 1
                  });
                }
              });
            }
          });
        });
      }
    });

    return Array.from(ingredientMap.values()).map(item => ({
      name: item.name,
      amount: item.count > 1 ? `${item.amount} x${item.count}` : item.amount,
      linkedIngredientId: item.linkedIngredientId
    }));
  }, [scheduleByDate]);

  return {
    scheduleByDate,
    setScheduleByDate,
    selectedDate,
    setSelectedDate,
    currentSchedule,
    handleMealComplete,
    handleAddDishStart,
    handleAddDishConfirm,
    handleAddToPlanConfirm,
    addDishTargetMeal,
    setAddDishTargetMeal,
    // 新增
    copySchedule,
    deleteSchedule,
    moveSchedule,
    deleteMealItem,
    updateMealItem,
    calculateStats,
    getDayNutrition,
    generateShoppingListFromSchedule
  };
};
